/**
 * Configuration — Compliance (Screen 1 Compliance Rules, Screen 2 Compliance Rule).
 * Licence, Badge, and Ticket rules for Business account trust items.
 */

export const COMPLIANCE_TYPE_OPTIONS = [
  { value: 'licence', label: 'Licence' },
  { value: 'badge', label: 'Badge' },
  { value: 'trade_ticket', label: 'Ticket' },
];

export const COMPLIANCE_NAME_BY_TYPE = {
  licence: [
    { value: 'electrical_licence', label: 'Electrical Licence' },
    { value: 'plumbing_licence', label: 'Plumbing Licence' },
    { value: 'gasfitting_licence', label: 'Gasfitting Licence' },
    { value: 'refrigeration_ac_licence', label: 'Refrigeration & Air Conditioning Licence' },
    { value: 'building_licence', label: 'Building Licence' },
    { value: 'security_installer_licence', label: 'Security Installer Licence' },
    { value: 'demolition_licence', label: 'Demolition Licence' },
  ],
  badge: [
    { value: 'police_check', label: 'Police Check' },
    { value: 'working_with_children_check', label: 'Working With Children Check' },
    { value: 'auspost_id_verification', label: 'AusPost ID Verification' },
  ],
  trade_ticket: [
    { value: 'white_card', label: 'White Card' },
    { value: 'working_at_heights', label: 'Working at Heights' },
    { value: 'confined_space', label: 'Confined Space' },
    { value: 'ewp', label: 'EWP' },
    { value: 'skid_steer', label: 'Skid Steer' },
  ],
};

export const APPLICATION_ACCESS_OPTIONS = [
  { value: 'allowed', label: 'Allowed' },
  { value: 'blocked', label: 'Blocked' },
];

export const REAPPLICATION_RULE_OPTIONS = [
  { value: 'blocked', label: 'Blocked' },
  { value: 'allowed_after_rejection', label: 'Allowed After Rejection' },
];

export const EVIDENCE_REQUIREMENT_OPTIONS = [
  { value: 'not_required', label: 'Not Required' },
  { value: 'optional', label: 'Optional' },
  { value: 'required', label: 'Required' },
];

export const INFORMATION_REQUEST_RULE_OPTIONS = [
  { value: 'not_allowed', label: 'Not Allowed' },
  { value: 'allowed_before_decision', label: 'Allowed Before Decision' },
];

export const DECISION_PATH_OPTIONS = [
  { value: 'approve_or_reject', label: 'Approve or Reject' },
  { value: 'approve_reject_rmi', label: 'Approve, Reject or Request More Information' },
];

export const DECISION_NOTES_OPTIONS = [
  { value: 'optional', label: 'Optional' },
  { value: 'required_on_decision', label: 'Required on Decision' },
];

export const EXPIRY_APPLIES_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'yes', label: 'Yes' },
];

export const PRE_EXPIRY_CHECK_OPTIONS = [
  { value: 'not_required', label: 'Not Required' },
  { value: 'required', label: 'Required' },
];

export const RENEWAL_RULE_OPTIONS = [
  { value: 'renewal_not_allowed', label: 'Renewal Not Allowed' },
  { value: 'new_submission_required', label: 'New Submission Required' },
  { value: 'renewal_submission_allowed', label: 'Renewal Submission Allowed' },
  { value: 'renewal_requires_approval_before_display', label: 'Renewal Requires Approval Before Display' },
];

export const EXPIRY_RULE_OPTIONS = [
  { value: 'hide_on_expiry', label: 'Hide on Expiry' },
  { value: 'keep_hidden_until_renewal_approved', label: 'Keep Hidden Until Renewal Approved' },
];

export const DISPLAY_RULE_OPTIONS = [
  { value: 'never_display', label: 'Never Display' },
  { value: 'display_when_eligible', label: 'Display When Eligible' },
];

export const COMPLIANCE_STATE_RESULTS = [
  { action: 'Approve', result: 'Approved' },
  { action: 'Reject', result: 'Rejected' },
  { action: 'Request More Information', result: 'Awaiting Resubmission' },
  { action: 'Recorded Expiry Date Reached', result: 'Expired' },
];

export const COMPLIANCE_STATE_RESULT_DEFINITIONS = [
  {
    field: 'approveStateResult',
    label: 'Approved Result',
    value: 'approved',
    options: [{ value: 'approved', label: 'Approved' }],
  },
  {
    field: 'rejectStateResult',
    label: 'Rejected Result',
    value: 'rejected',
    options: [{ value: 'rejected', label: 'Rejected' }],
  },
  {
    field: 'requestMoreInformationStateResult',
    label: 'Awaiting Resubmission Result',
    value: 'awaiting_resubmission',
    options: [{ value: 'awaiting_resubmission', label: 'Awaiting Resubmission' }],
    requiresInformationRequest: true,
  },
  {
    field: 'expiredStateResult',
    label: 'Expired Result',
    value: 'expired',
    options: [{ value: 'expired', label: 'Expired' }],
    requiresExpiry: true,
  },
];

export function getVisibleComplianceStateResults(form) {
  const normalized = normalizeComplianceForm(form || {});
  return COMPLIANCE_STATE_RESULT_DEFINITIONS.filter((definition) => {
    if (definition.requiresInformationRequest) {
      return normalized.informationRequestRule === 'allowed_before_decision';
    }
    if (definition.requiresExpiry) {
      return normalized.expiryApplies === 'yes';
    }
    return true;
  });
}

function defaultComplianceStateResults() {
  return {
    approveStateResult: 'approved',
    rejectStateResult: 'rejected',
    requestMoreInformationStateResult: 'awaiting_resubmission',
    expiredStateResult: 'expired',
  };
}

/** @deprecated */
export const MORE_INFORMATION_ALLOWED_OPTIONS = INFORMATION_REQUEST_RULE_OPTIONS;

/** @deprecated */
export const YES_NO_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'yes', label: 'Yes' },
];

const LEGACY_EVIDENCE_MAP = {
  no: 'not_required',
  yes: 'required',
};

const LEGACY_INFO_REQUEST_MAP = {
  no: 'not_allowed',
  yes_before_decision: 'allowed_before_decision',
};

const LEGACY_REAPPLICATION_MAP = {
  allowed: 'allowed_after_rejection',
  allowed_after_expiry: 'blocked',
};

export function labelFromOptions(options, value) {
  if (value == null || value === '') return '';
  const o = options?.find((x) => x.value === value);
  return o?.label ?? value;
}

export function normalizeComplianceRuleStatus(status) {
  const s = String(status || '').toLowerCase().trim();
  if (s === 'draft' || s === 'active' || s === 'inactive') return s;
  return '';
}

export function getComplianceNamesForType(complianceType) {
  return COMPLIANCE_NAME_BY_TYPE[complianceType] || [];
}

export function getDecisionPathOptionsForInfoRequest(informationRequestRule) {
  if (informationRequestRule === 'allowed_before_decision') {
    return DECISION_PATH_OPTIONS;
  }
  return DECISION_PATH_OPTIONS.filter((o) => o.value === 'approve_or_reject');
}

export function decisionPathMatchesInfoRequest(decisionPath, informationRequestRule) {
  if (informationRequestRule === 'allowed_before_decision') return true;
  return decisionPath === 'approve_or_reject';
}

export function normalizeComplianceForm(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const evidenceRequirement =
    raw.evidenceRequirement ??
    LEGACY_EVIDENCE_MAP[raw.supportingEvidenceRequired] ??
    raw.supportingEvidenceRequired ??
    '';
  const informationRequestRule =
    raw.informationRequestRule ??
    LEGACY_INFO_REQUEST_MAP[raw.moreInformationAllowed] ??
    raw.moreInformationAllowed ??
    '';
  let reapplicationRule = raw.reapplicationRule ?? '';
  if (LEGACY_REAPPLICATION_MAP[reapplicationRule]) {
    reapplicationRule = LEGACY_REAPPLICATION_MAP[reapplicationRule];
  }
  if (!REAPPLICATION_RULE_OPTIONS.some((o) => o.value === reapplicationRule) && reapplicationRule === 'allowed') {
    reapplicationRule = 'allowed_after_rejection';
  }

  const expiryApplies = raw.expiryApplies ?? 'no';
  const preExpiryCheck =
    raw.preExpiryCheck ??
    (raw.expiryReminder && raw.expiryReminder !== 'on_expiry_date' ? 'required' : 'not_required');
  const stateDefaults = defaultComplianceStateResults();

  return {
    ...raw,
    approveStateResult: raw.approveStateResult ?? raw.approvedResult ?? stateDefaults.approveStateResult,
    rejectStateResult: raw.rejectStateResult ?? raw.rejectedResult ?? stateDefaults.rejectStateResult,
    requestMoreInformationStateResult:
      raw.requestMoreInformationStateResult ?? stateDefaults.requestMoreInformationStateResult,
    expiredStateResult: raw.expiredStateResult ?? raw.expiredResult ?? stateDefaults.expiredStateResult,
    evidenceRequirement,
    supportingEvidenceRequired: evidenceRequirement,
    informationRequestRule,
    moreInformationAllowed: informationRequestRule,
    reapplicationRule,
    preExpiryCheck: expiryApplies === 'yes' ? preExpiryCheck : '',
    preExpiryLeadTime: expiryApplies === 'yes' ? raw.preExpiryLeadTime ?? '' : '',
    expiryRule: expiryApplies === 'yes' ? raw.expiryRule ?? '' : '',
    status: normalizeComplianceRuleStatus(raw.status) || 'draft',
  };
}

export function findDuplicateComplianceRule(rules, candidate, excludeId) {
  const normalized = normalizeComplianceForm(candidate);
  return (rules || []).find((r) => {
    if (excludeId && r.id === excludeId) return false;
    if (normalizeComplianceRuleStatus(r.status) !== 'active') return false;
    return r.complianceType === normalized.complianceType && r.complianceName === normalized.complianceName;
  });
}

export function findConflictingComplianceRule(rules, candidate, excludeId) {
  const normalized = normalizeComplianceForm(candidate);
  const dup = findDuplicateComplianceRule(rules, normalized, excludeId);
  if (dup) return dup;

  if (!decisionPathMatchesInfoRequest(normalized.decisionPath, normalized.informationRequestRule)) {
    return { id: 'conflict', reason: 'decision_path' };
  }

  if (normalized.expiryApplies === 'no' && (normalized.preExpiryCheck || normalized.expiryRule || normalized.preExpiryLeadTime)) {
    return { id: 'conflict', reason: 'expiry_when_not_applicable' };
  }

  if (
    normalized.expiryApplies === 'yes' &&
    normalized.preExpiryCheck === 'required' &&
    !String(normalized.preExpiryLeadTime || '').trim()
  ) {
    return { id: 'conflict', reason: 'lead_time' };
  }

  if (
    normalized.displayRule === 'display_when_eligible' &&
    normalized.expiryRule === 'keep_hidden_until_renewal_approved' &&
    normalized.renewalRule === 'renewal_not_allowed'
  ) {
    return { id: 'conflict', reason: 'display_renewal' };
  }

  return null;
}

export function nextComplianceRuleId(rules) {
  const nums = (rules || []).map((r) => {
    const m = String(r.id || '').match(/^COM-(\d+)$/i);
    return m ? parseInt(m[1], 10) : 0;
  });
  const max = nums.length ? Math.max(...nums) : 0;
  return `COM-${String(max + 1).padStart(4, '0')}`;
}

function baseSample(overrides) {
  return normalizeComplianceForm({
    status: 'draft',
    applicationAccess: 'allowed',
    reapplicationRule: 'allowed_after_rejection',
    evidenceRequirement: 'optional',
    informationRequestRule: 'not_allowed',
    decisionPath: 'approve_or_reject',
    decisionNotes: 'optional',
    expiryApplies: 'yes',
    preExpiryCheck: 'required',
    preExpiryLeadTime: '30 days',
    renewalRule: 'renewal_submission_allowed',
    expiryRule: 'hide_on_expiry',
    displayRule: 'display_when_eligible',
    version: 1,
    createdOn: '2025-01-15T09:00:00.000Z',
    createdBy: 'System Admin',
    modifiedOn: '2025-01-15T09:00:00.000Z',
    modifiedBy: 'System Admin',
    ...overrides,
  });
}

export function buildSampleComplianceRules() {
  return [
    baseSample({
      id: 'COM-0001',
      ruleName: 'Electrical Licence Rule',
      complianceType: 'licence',
      complianceName: 'electrical_licence',
    }),
    baseSample({
      id: 'COM-0002',
      ruleName: 'Plumbing Licence Rule',
      complianceType: 'licence',
      complianceName: 'plumbing_licence',
    }),
    baseSample({
      id: 'COM-0003',
      ruleName: 'Gasfitting Licence Rule',
      complianceType: 'licence',
      complianceName: 'gasfitting_licence',
    }),
    baseSample({
      id: 'COM-0004',
      ruleName: 'Building Licence Rule',
      complianceType: 'licence',
      complianceName: 'building_licence',
      informationRequestRule: 'allowed_before_decision',
      decisionPath: 'approve_reject_rmi',
      renewalRule: 'renewal_requires_approval_before_display',
      expiryRule: 'keep_hidden_until_renewal_approved',
    }),
    baseSample({
      id: 'COM-0005',
      ruleName: 'Police Check Badge Rule',
      complianceType: 'badge',
      complianceName: 'police_check',
    }),
    baseSample({
      id: 'COM-0006',
      ruleName: 'Working With Children Badge Rule',
      complianceType: 'badge',
      complianceName: 'working_with_children_check',
    }),
    baseSample({
      id: 'COM-0007',
      ruleName: 'White Card Rule',
      complianceType: 'trade_ticket',
      complianceName: 'white_card',
    }),
    baseSample({
      id: 'COM-0008',
      ruleName: 'Working at Heights Rule',
      complianceType: 'trade_ticket',
      complianceName: 'working_at_heights',
      informationRequestRule: 'allowed_before_decision',
      decisionPath: 'approve_reject_rmi',
      renewalRule: 'renewal_requires_approval_before_display',
      expiryRule: 'keep_hidden_until_renewal_approved',
    }),
    baseSample({
      id: 'COM-0009',
      ruleName: 'Confined Space Rule',
      complianceType: 'trade_ticket',
      complianceName: 'confined_space',
      informationRequestRule: 'allowed_before_decision',
      decisionPath: 'approve_reject_rmi',
      renewalRule: 'renewal_requires_approval_before_display',
      expiryRule: 'keep_hidden_until_renewal_approved',
    }),
    baseSample({
      id: 'COM-0010',
      ruleName: 'EWP Rule',
      complianceType: 'trade_ticket',
      complianceName: 'ewp',
    }),
  ];
}

export function getExpiryRuleTableLabel(rule) {
  const normalized = normalizeComplianceForm(rule);
  if (normalized.expiryApplies !== 'yes') return '—';
  return labelFromOptions(EXPIRY_RULE_OPTIONS, normalized.expiryRule) || '—';
}

export function getDisplayRuleTableLabel(rule) {
  return labelFromOptions(DISPLAY_RULE_OPTIONS, normalizeComplianceForm(rule).displayRule) || '—';
}

export function getComplianceTypeLabel(type) {
  return labelFromOptions(COMPLIANCE_TYPE_OPTIONS, type);
}

export function getComplianceNameLabel(type, name) {
  return labelFromOptions(getComplianceNamesForType(type), name);
}
